import type { QueryClient } from '@tanstack/react-query'
import type { LeadsResponse } from '@/shared/api/core'
import { LEAD_LISTS, rowAsLead, toListRow } from '@/shared/api/leadLists'
import type { Lead, TimelineItem } from '@/shared/types'

/**
 * Optimistic writes, straight into the React Query cache.
 *
 * The queue used to keep a useState mirror of the lead list beside the cache,
 * plus a ref of outstanding patches to re-apply whenever a refetch landed
 * mid-mutation. Two sources of truth for the same rows, and because the mirror
 * lived in a hook, `setLeads` and the patch ref had to be threaded into
 * useLeadMutations -- which is what forced every mutation callback to be
 * prop-drilled from the shell into Focus, Table and the overlay.
 *
 * One source now. The optimistic value goes into the cache, so every reader
 * sees it without anything being passed down, and the server's canonical row
 * replaces it on settle. A refetch cannot clobber a pending patch because
 * onMutate cancels in-flight lead queries first, and refetchOnWindowFocus is
 * off for the whole app.
 */

export const LEADS_KEY = ['leads'] as const

/** Apply a change to one lead everywhere it is cached; returns an undo. */
export function editLeadInCache(
  qc: QueryClient,
  id: string,
  edit: (lead: Lead) => Lead,
): () => void {
  const lists = qc.getQueriesData<LeadsResponse>(LEAD_LISTS)
  const single = qc.getQueryData<Lead>(['lead', id])

  // A list row is edited as the card it stands for and cut back to a row, so
  // an edit to a card-only field (a timeline entry, the comment) leaves the
  // row as it was.
  qc.setQueriesData<LeadsResponse>(LEAD_LISTS, old =>
    old ? { ...old, leads: old.leads.map(l => (l.id === id ? toListRow(edit(rowAsLead(l))) : l)) } : old,
  )
  if (single) qc.setQueryData<Lead>(['lead', id], edit(single))

  // Restores exactly what was there, which is what makes a failed mutation
  // leave no trace rather than un-applying a guess.
  return () => {
    for (const [key, data] of lists) qc.setQueryData(key, data)
    if (single) qc.setQueryData(['lead', id], single)
  }
}

// ── the edits themselves, kept pure so they can be checked directly ──────────

// A new promise date makes the server's band stale, so drop it and let the
// local rule band the lead until the refetch brings the server's answer.
export const mergeFields = (patch: Partial<Lead>) => (lead: Lead): Lead =>
  'nextContactAt' in patch
    ? { ...lead, ...patch, queueBand: undefined }
    : { ...lead, ...patch }

export const appendTimelineItem = (item: TimelineItem) => (lead: Lead): Lead =>
  ({ ...lead, timeline: [...lead.timeline, item] })

export const markSkipped = (lead: Lead): Lead => ({ ...lead, skipped: true })
